<template>
  <modal-wrapper
    ref="modalRef"
    :busy="isValidatingSubmit"
    :close-on-ok-click="false"
    :content-overflow-y="'visible'"
    :header="t('filesOverview.newUpload')"
    :ok-button-label="t('generic.select')"
    :size="ModalSize.L"
    @ok-button-click="handleModalOK"
    @show="onModalShow"
  >
    <div id="modal-container">
      <div
        v-if="isUserRole(supplierSelectionAllowedRoles)"
        class="input-container"
      >
        <label for="suppliers-input">{{ t('generic.supplier', 1) }}</label>
        <div class="select-container">
          <styled-multiselect
            v-model="selectedSupplier"
            class="multiselect"
            :options="allSuppliers"
            :placeholder="t('generic.suppliers')"
            :loading="isLoadingSuppliers"
            label="name"
            track-by="name"
          ></styled-multiselect>
          <p
            v-if="!errorMessages.supplier && isSubmitted"
            class="error-message"
          >
            {{ t('filesOverview.provideSupplier') }}
          </p>
        </div>
      </div>

      <div class="input-container">
        <label for="upload-id-input">{{ t('filesOverview.uploadId') }}</label>
        <div class="select-container">
          <PVInputText
            id="upload-id-input"
            v-model="referenceNumber"
            type="text"
          />
          <p
            v-if="!errorMessages.referenceNumber && isSubmitted"
            class="error-message"
          >
            {{ t('filesOverview.provideUploadId') }}
          </p>
          <p
            v-else-if="!errorMessages.referenceNumberUnique && isSubmitted"
            class="error-message"
          >
            {{ t('documentUpload.referenceNumberError') }}
          </p>
        </div>
      </div>

      <div class="input-container">
        <label>{{ t('filesOverview.selectContentType') }}</label>
        <div class="select-container">
          <styled-multiselect
            v-model="selectedContentType"
            class="multiselect"
            :options="allContentTypes"
            :placeholder="t('projectDetails.contentTypes.selectContentType')"
            :loading="isLoadingContentTypes"
            label="contentTypeName"
            track-by="contentTypeName"
          ></styled-multiselect>
          <p
            v-if="!errorMessages.contentType && isSubmitted"
            class="error-message"
          >
            {{ t('filesOverview.provideContentType') }}
          </p>
          <p v-if="isNoMappingAssignedError" class="error-message">
            {{ t('documentUpload.noMappingAssigned') }}
          </p>
        </div>
      </div>

      <div class="input-container">
        <label>{{ $tt('filesOverview.checkBotConfiguration') }}</label>
        <div class="select-container">
          <check-bot-config-select
            v-model="selectedCheckBotConfig"
            :is-waiting-for-supplier="!selectedSupplier"
            :project-id="projectId"
            :supplier-id="selectedSupplier?.id"
          />
          <p v-if="isCheckBotConfigError" class="error-message">
            {{ $tt('filesOverview.provideCheckBotConfig') }}
          </p>
        </div>
      </div>
    </div>
  </modal-wrapper>
</template>
<script setup lang="ts">
  import { ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios, { AxiosResponse } from 'axios';
  import StyledMultiselect from '../../components/StyledMultiselect/StyledMultiselect.vue';
  import endpointsList from '../../helpers/endpointsList';
  import {
    IProjectAssignments,
    IValidateReferenceNumberDto,
    IContentType,
    ISupplier,
    Role,
    ICheckBotConfigSummaryDto,
    ProjectType,
  } from 'types';
  import { useModalWrapper } from '../ModalWrapper';
  import ModalWrapper from '../ModalWrapper/ModalWrapper.vue';
  import { ModalSize } from '../ModalWrapper/ModalWrapper.types';
  import { computed } from 'vue';
  import { PROJECT_TYPE_PARAM, Route, routeParams } from '@/routes';
  import { useRoles } from '@/composables/useRoles';
  import CheckBotConfigSelect from '../CheckBotConfigSelect/CheckBotConfigSelect.vue';
  import { useStore } from 'vuex';

  interface ISupplierOption {
    name: string;
    id: string;
    SAPNumber: string;
  }

  const props = defineProps<{
    projectId: string;
    projectNumber: string;
    projectName: string;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const { createToast } = useToast();
  const { isUserRole } = useRoles();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const store = useStore();

  const allContentTypes = ref<IProjectAssignments[]>([]);
  const selectedContentType = ref<null | IContentType>(null);
  const isLoadingContentTypes = ref(false);
  const referenceNumber = ref('');

  const selectedSupplier = ref<null | ISupplierOption>(null);
  const allSuppliers = ref<ISupplierOption[]>([]);
  const isLoadingSuppliers = ref(false);

  const selectedCheckBotConfig = ref<ICheckBotConfigSummaryDto | null>(null);

  const isValidated = ref(false);
  const isValidatingSubmit = ref(false);
  const errorMessages = ref({
    supplier: false,
    referenceNumber: false,
    referenceNumberUnique: false,
    contentType: false,
    checkBotConfig: false,
  });
  const isSubmitted = ref(false);

  const isNoMappingAssignedError = ref(false);

  const isCheckBotConfigError = computed(
    () => isSubmitted.value && selectedCheckBotConfig.value === null,
  );

  const supplierSelectionAllowedRoles = ref<Role[]>([Role.Admin]);

  async function getContentTypesForProject() {
    isLoadingContentTypes.value = true;

    try {
      const response = await axios.get(
        endpointsList.contentTypeMappings.getContentTypesAssignmentsForProject(
          props.projectId,
        ),
      );
      allContentTypes.value = response.data;
    } catch {
      createToast(t('errors.error'), t('errors.error'), 'danger');
    } finally {
      isLoadingContentTypes.value = false;
    }
  }

  async function getSuppliersForProject() {
    isLoadingSuppliers.value = true;

    try {
      const response: AxiosResponse<
        Pick<ISupplier, '_id' | 'name' | 'SAPNumber'>[]
      > = await axios.get(
        endpointsList.projects.getProjectSuppliersById(props.projectId, [
          '_id',
          'name',
          'SAPNumber',
        ]),
      );
      allSuppliers.value = response.data.map((supplier) => ({
        name: supplier.name,
        id: supplier._id.toString(),
        SAPNumber: supplier.SAPNumber,
      }));
      /**
       * The supplier is selected automatically if the user is a coworker.
       */
      if (isUserRole([Role.Coworker])) {
        const supplierId = store.state.supplierId;
        const supplier = allSuppliers.value.find(
          (supplier) => supplier.id === supplierId,
        );
        if (!supplier) {
          return;
        }
        selectedSupplier.value = supplier;
      }
    } catch {
      createToast(t('errors.error'), t('errors.error'), 'danger');
    } finally {
      isLoadingSuppliers.value = false;
    }
  }

  async function handleModalOK() {
    await validateSubmit();
    isSubmitted.value = true;
    if (
      !isValidated.value ||
      !selectedContentType.value ||
      !selectedSupplier.value
    ) {
      return;
    }

    const queryParams = routeParams[Route.DOCUMENT_UPLOAD].query;
    router.push({
      name: Route.DOCUMENT_UPLOAD,
      query: {
        [queryParams[PROJECT_TYPE_PARAM]]: ProjectType.SUPPLIER_PROJECT,
        [queryParams.referenceNumber]: referenceNumber.value,
        [queryParams.contentTypeId]: selectedContentType.value.contentTypeId,
        [queryParams.projectId]: props.projectId,
        [queryParams.projectName]: props.projectName,
        [queryParams.supplierId]: selectedSupplier.value.id,
        [queryParams.checkBotConfigId]: selectedCheckBotConfig.value?._id,
      },
    });
  }

  function checkIfSupplierHasContentTypeMapping() {
    if (!selectedContentType.value) {
      return false;
    }

    const assignment = selectedContentType.value.assignments.find(
      (assignment) =>
        assignment.supplierId?.toString() === selectedSupplier.value?.id,
    );

    return assignment ? (assignment as any).isMapping : false;
  }

  async function validateSubmit() {
    isValidatingSubmit.value = true;
    errorMessages.value.supplier = !!selectedSupplier.value;
    errorMessages.value.referenceNumber = !!referenceNumber.value;
    errorMessages.value.contentType = !!selectedContentType.value;
    errorMessages.value.checkBotConfig = !!selectedCheckBotConfig.value;
    if (referenceNumber.value) {
      await checkIsReferenceNumberUnique();
    }

    if (Object.values(errorMessages.value).some((value) => !value)) {
      isValidated.value = false;
      isValidatingSubmit.value = false;
      return;
    }

    if (!checkIfSupplierHasContentTypeMapping()) {
      isNoMappingAssignedError.value = true;
      isValidated.value = false;
      isValidatingSubmit.value = false;
      return;
    }

    isValidated.value = true;
    isValidatingSubmit.value = false;
  }

  async function checkIsReferenceNumberUnique() {
    if (!referenceNumber.value) return;
    const requestBody: IValidateReferenceNumberDto = {
      referenceNumber: referenceNumber.value,
    };
    try {
      const response: AxiosResponse<boolean> = await axios.post(
        endpointsList.documentsUploads.validateReferenceNumber(props.projectId),
        requestBody,
      );

      errorMessages.value.referenceNumberUnique = response.data;
    } catch {
      createToast(
        t('errors.error'),
        t('documentUpload.errors.validationError'),
        'danger',
      );
    }
  }

  function onModalShow() {
    getContentTypesForProject();
    getSuppliersForProject();
  }
</script>

<style scoped lang="scss">
  #modal-container {
    display: flex;
    flex-direction: column;
    row-gap: 30px;

    .error-message {
      color: red;
      font-size: 16px;
      line-height: 20px;
      margin: 0;
      text-align: center;
      margin-top: 20px;
    }

    .input-container {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 20px;

      label {
        width: 20%;
      }

      .select-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;

        .multiselect,
        #upload-id-input {
          min-width: 0;
          width: 80%;
        }
      }
    }
  }
</style>
